/**
 * The low-level HTTP transport every typed endpoint function in this
 * package is built on (docs/APP_PLAN.md §5.2, §12 M2b).
 *
 * Responsibilities, all satisfied on every mutating request so the API's
 * CSRF defense (`apps/api/src/plugins/security.ts`, docs/APP_PLAN.md §9.4)
 * never rejects a request this client sends:
 *
 *   - `credentials: 'include'` so the `HttpOnly` session cookie travels
 *     with every request (same-origin in production; the Vite dev server
 *     proxies `/api` so requests stay same-origin in development too —
 *     docs/APP_PLAN.md §11.1).
 *   - The custom client header named by `CSRF_CLIENT_HEADER_NAME` in
 *     `@perplexicon/config` on every `POST`/`PUT`/`PATCH`/`DELETE`, the
 *     header a cross-origin form cannot set without a failing CORS
 *     preflight.
 *   - Decodes the uniform error envelope `{ error: { code, message,
 *     details?, requestId? } }` into a typed `ApiError` keyed on the
 *     stable `code` (never `message` text) on any non-2xx response.
 *   - Surfaces anything that isn't a decodable envelope (network failure,
 *     non-JSON body, malformed envelope) as `ApiNetworkError`.
 *
 * This module has no Node-only, DOM-only, or React dependency — only the
 * standard `fetch`/`Headers`/`AbortSignal` globals available in browsers,
 * Node 24, and React Native/Expo, so a future mobile client can reuse it
 * unchanged (APP_SPEC §4, TECH_STACK §83–§85).
 */
import { CSRF_CLIENT_HEADER_NAME } from '@perplexicon/config';
import type { ErrorCode } from '@perplexicon/shared';

import { ApiError, ApiNetworkError } from './errors.js';

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiClientOptions {
  /**
   * Base URL every request path is resolved against. Defaults to `/api`,
   * which — combined with the Vite dev proxy in development and Caddy's
   * `/api/*` route in production — keeps every request same-origin
   * (docs/APP_PLAN.md §9.4, §11.1, §11.2).
   */
  baseUrl?: string;
  /**
   * The `fetch` implementation to use. Defaults to the global `fetch`.
   * Overridable for tests and for any future runtime lacking a global
   * `fetch` (older React Native).
   */
  fetchImpl?: typeof fetch;
  /**
   * Value sent in the required CSRF client header. Only its *presence* is
   * checked server-side (docs/APP_PLAN.md §9.4); the value is purely
   * informational for server-side logging/debugging. Defaults to `'web'`.
   */
  clientName?: string;
}

export interface RequestOptions {
  method: HttpMethod;
  path: string;
  body?: unknown;
  /**
   * Extra request headers, merged in after the CSRF client header and
   * `content-type` are set (and able to override neither by key
   * collision in practice, since callers only ever add distinct headers
   * such as `Idempotency-Key` — docs/APP_PLAN.md §8.3).
   */
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/** Shape of the uniform error envelope every API failure response uses (TECH_STACK §52). */
interface ErrorEnvelopeBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
    requestId?: string;
  };
}

function isErrorEnvelopeBody(value: unknown): value is ErrorEnvelopeBody {
  if (typeof value !== 'object' || value === null || !('error' in value)) return false;
  const err = value.error;
  if (typeof err !== 'object' || err === null) return false;
  const code = (err as { code: unknown }).code;
  const message = (err as { message: unknown }).message;
  return typeof code === 'string' && typeof message === 'string';
}

export class HttpClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly clientName: string;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? '/api';
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.clientName = options.clientName ?? 'web';
  }

  /**
   * The resolved base URL every request path is joined onto — exposed so a
   * transport built alongside `HttpClient` but outside its `request()` flow
   * (e.g. `subscribeToSession`'s `EventSource`, which cannot go through
   * `fetch`) can still build a same-origin, same-base URL without
   * duplicating the default.
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /** Issues a request and decodes the JSON response body as `T`. Use for endpoints that always return a body. */
  async request<T>(options: RequestOptions): Promise<T> {
    const result = await this.requestMaybeEmpty<T>(options);
    if (result === undefined) {
      throw new ApiNetworkError('Expected a response body but received an empty response.');
    }
    return result;
  }

  /** Issues a request that may legitimately return no body (e.g. `204 No Content`). */
  async requestMaybeEmpty<T>(options: RequestOptions): Promise<T | undefined> {
    const url = `${this.baseUrl}${options.path}`;
    const headers = new Headers();
    if (MUTATING_METHODS.has(options.method)) {
      headers.set(CSRF_CLIENT_HEADER_NAME, this.clientName);
    }
    if (options.body !== undefined) {
      headers.set('content-type', 'application/json');
    }
    if (options.headers) {
      for (const [key, value] of Object.entries(options.headers)) {
        headers.set(key, value);
      }
    }

    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        method: options.method,
        headers,
        credentials: 'include',
        signal: options.signal,
        body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      });
    } catch (cause) {
      throw new ApiNetworkError(
        'Unable to reach the server. Check your connection and try again.',
        cause,
      );
    }

    if (response.status === 204 || response.status === 205) {
      return undefined;
    }

    const rawText = await response.text();
    const parsed = parseJson(rawText);

    if (response.ok) {
      if (rawText.length === 0) return undefined;
      if (parsed === UNPARSEABLE) {
        throw new ApiNetworkError('Received a response that was not valid JSON.');
      }
      return parsed as T;
    }

    if (parsed !== UNPARSEABLE && isErrorEnvelopeBody(parsed)) {
      throw new ApiError(
        parsed.error.code as ErrorCode | 'UNKNOWN_ERROR',
        parsed.error.message,
        response.status,
        parsed.error.details,
        parsed.error.requestId,
      );
    }

    throw new ApiNetworkError(
      `Server responded with status ${String(response.status)} and no decodable error body.`,
    );
  }
}

const UNPARSEABLE = Symbol('unparseable');

function parseJson(text: string): unknown {
  if (text.length === 0) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return UNPARSEABLE;
  }
}
